import * as moment from "moment-timezone";
import { getIncidentRecordBranchId } from "./incidentBranchMeta.ts";

export function resolveIncidentFinalStatus(incident: any): boolean | null {
  if (!incident) return null;
  if (
    incident.createdAt &&
    incident.updateAt &&
    incident.createdAt === incident.updateAt
  ) {
    if (incident.type === "Aceptar") return true;
    if (incident.type === "Rechazar") return false;
  }
  if (typeof incident.finalStatus === "boolean") return incident.finalStatus;
  if (incident.type === "Aceptar") return true;
  if (incident.type === "Rechazar") return false;
  return null;
}

export function isNoProgramadoIncident(incident: any): boolean {
  return String(incident?.status || "").trim() === "No programado";
}

/** No programado rechazado (líder o RH): no cuenta en nómina. */
export function isRejectedUnscheduledIncident(incident: any): boolean {
  if (!isNoProgramadoIncident(incident)) {
    return false;
  }
  const resolved = resolveIncidentFinalStatus(incident);
  if (resolved === false) {
    return true;
  }
  if (resolved === true) {
    return false;
  }
  if (incident.authorizeBy && String(incident.type || "").trim() === "Rechazar") {
    return true;
  }
  return false;
}

export function payrollVisitPairKey(visit: {
  entradaRegistrada?: string;
  salidaRegistrada?: string;
  branchId?: number;
  checkIn?: { assistanceCheck_id?: number; id?: number; checkTime?: string };
  checkOut?: { assistanceCheck_id?: number; id?: number; checkTime?: string };
}): string {
  const inId = visit.checkIn?.assistanceCheck_id ?? visit.checkIn?.id;
  const outId = visit.checkOut?.assistanceCheck_id ?? visit.checkOut?.id;
  if (inId != null && outId != null) {
    return `id:${inId}|${outId}`;
  }
  return `t:${visit.entradaRegistrada || ""}|${visit.salidaRegistrada || ""}|${visit.branchId ?? ""}`;
}

export function incidentVisitPairKey(incident: any): string {
  const checkIn =
    incident?.schedule?.assistanceCheck ??
    incident?.checkIn ??
    incident?.schedule?.checkIn;
  const checkOut =
    incident?.schedule?.assistanceCheckout ??
    incident?.checkOut ??
    incident?.schedule?.checkOut;
  const inId = checkIn?.id ?? checkIn?.assistanceCheck_id;
  const outId = checkOut?.id ?? checkOut?.assistanceCheckout_id;
  if (inId != null && outId != null) {
    return `id:${inId}|${outId}`;
  }
  const inTime =
    checkIn?.checkTime ??
    checkIn?.assistanceCheck_checkTime ??
    incident?.schedule?.assistanceCheck?.check_time;
  const outTime =
    checkOut?.checkTime ??
    checkOut?.assistanceCheck_checkTime ??
    incident?.schedule?.assistanceCheckout?.check_time;
  const branchId = getIncidentRecordBranchId(incident);
  return `t:${inTime || ""}|${outTime || ""}|${branchId}`;
}

export function visitMatchesUnscheduledIncident(
  visit: Parameters<typeof payrollVisitPairKey>[0],
  incident: any
): boolean {
  if (!isNoProgramadoIncident(incident)) {
    return false;
  }
  return payrollVisitPairKey(visit) === incidentVisitPairKey(incident);
}

export function visitIsRejectedUnscheduled(
  visit: Parameters<typeof payrollVisitPairKey>[0],
  incidents: any[] = []
): boolean {
  return (incidents || []).some(
    (inc) =>
      isRejectedUnscheduledIncident(inc) &&
      visitMatchesUnscheduledIncident(visit, inc)
  );
}

export function filterPayrollVisitsExcludingRejectedUnscheduled<
  T extends Parameters<typeof payrollVisitPairKey>[0],
>(visits: T[], incidents: any[] = []): T[] {
  if (!visits?.length) {
    return [];
  }
  const rejected = (incidents || []).filter(isRejectedUnscheduledIncident);
  if (!rejected.length) {
    return visits;
  }
  return visits.filter((visit) => !visitIsRejectedUnscheduled(visit, rejected));
}

export function incidentsForOperationalDate(
  incidents: any[],
  dateYmd: string
): any[] {
  return (incidents || []).filter((inc) => {
    const d =
      inc.schedule?.assistanceCheck?.checkDate ??
      inc.schedule?.exactDate ??
      inc.checkIn?.checkDate;
    if (!d) return false;
    return moment.utc(d).format("YYYY-MM-DD") === dateYmd;
  });
}

export function effectivePayrollDayVisits(
  dp: {
    hours?: number;
    visits?: any[];
    checkIn?: string;
    checkOut?: string;
    branchId?: number;
  },
  incidents: any[] = []
): { visits: any[]; totalHours: number } {
  const rawVisits = dp.visits || [];
  if (rawVisits.length > 0) {
    const filtered = filterPayrollVisitsExcludingRejectedUnscheduled(
      rawVisits,
      incidents
    );
    const totalHours = filtered.reduce(
      (sum, visit) => sum + Number(visit.horasRegistradas ?? 0),
      0
    );
    return { visits: filtered, totalHours };
  }

  const synthetic = {
    entradaRegistrada: dp.checkIn,
    salidaRegistrada: dp.checkOut,
    branchId: dp.branchId,
    horasRegistradas: Number(dp.hours ?? 0),
  };
  if (visitIsRejectedUnscheduled(synthetic, incidents)) {
    return { visits: [], totalHours: 0 };
  }
  return { visits: rawVisits, totalHours: Number(dp.hours ?? 0) };
}
